"use client";

import type React from "react";
import {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useReducer,
} from "react";
import type { Product as IProduct } from "@/types/api";
import type { IApiCartItem } from "@/types/cart";
import {
  getCart as getShopifyCart,
  addToCart as addToShopifyCart,
  updateCartItem,
  removeCartItem,
} from "@/shopify/api/shopify-cart";
import { EventType, publishEvent } from "@/events";

// Types
export type CartItem = IApiCartItem;

interface CartState {
  items: CartItem[];
  totalItems: number;
  subtotal: number;
  isOpen: boolean;
  isLoading: boolean;
  error: string | null;
  inventoryWarnings: Record<string, string>;
}

type CartAction =
  | {
      type: "SET_CART_DATA";
      payload: { items: CartItem[]; totalItems: number; subtotal: number };
    }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "TOGGLE_CART"; payload?: boolean }
  | {
      type: "OPTIMISTIC_ADD";
      payload: { product: Partial<IProduct>; quantity: number };
    }
  | { type: "OPTIMISTIC_REMOVE"; payload: { productId: string } }
  | {
      type: "OPTIMISTIC_UPDATE";
      payload: { productId: string; quantity: number };
    }
  | { type: "OPTIMISTIC_CLEAR" };

interface CartContextType {
  items: CartItem[];
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  addToCart: (
    variantId: string,
    quantity: number,
    productInfo?: Partial<IProduct>,
  ) => Promise<void>;
  removeFromCart: (variantId: string) => Promise<void>;
  updateQuantity: (variantId: string, quantity: number) => Promise<void>;
  clearCart: () => Promise<void>;
  refreshCart: () => Promise<void>;
  totalItems: number;
  subtotal: number;
  isLoading: boolean;
  error: string | null;
}

// Utility functions
const calculateTotals = (
  items: CartItem[],
): { totalItems: number; subtotal: number } => {
  const totalItems = items.reduce((total, item) => total + item.quantity, 0);

  // Price is in cents, so we keep it in cents for consistency
  const subtotal = items.reduce((total, item) => {
    return total + (item.price || 0) * item.quantity;
  }, 0);

  return { totalItems, subtotal };
};

// Reducer function
const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case "SET_CART_DATA":
      return {
        ...state,
        items: action.payload.items,
        totalItems: action.payload.totalItems,
        subtotal: action.payload.subtotal,
        error: null,
      };
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload };
    case "TOGGLE_CART":
      return {
        ...state,
        isOpen: action.payload !== undefined ? action.payload : !state.isOpen,
      };
    case "OPTIMISTIC_ADD": {
      const { product, quantity } = action.payload;
      const existingItemIndex = state.items.findIndex(
        (item) => item.variant_id === product.variants?.[0]?.id,
      );

      let newItems: CartItem[];
      if (existingItemIndex >= 0) {
        // Update existing item
        newItems = [...state.items];
        newItems[existingItemIndex] = {
          ...newItems[existingItemIndex],
          quantity: newItems[existingItemIndex].quantity + quantity,
        };
      } else {
        // Add new item
        // Create a simplified cart item from the product
        const newItem: CartItem = {
          id: product.id || "",
          variant_id: product.variants?.[0]?.id || "",
          quantity,
          price:
            typeof product.price === "string"
              ? Number.parseFloat(product.price)
              : 0,
          original_price:
            typeof product.compare_at_price === "string"
              ? Number.parseFloat(product.compare_at_price)
              : 0,
          image: product.image?.url || "",
          featured_image: {
            url: product.image?.url || "",
          },
          handle: product.handle || "",
          variant_title: product.variants?.[0]?.title || "",
          product_id: product.id || "",
          product_title: product.title || "",
          // Add other required fields with default values
          key: "",
          properties: {},
          presentment_price: 0,
          discounted_price: 0,
          line_price: 0,
          original_line_price: 0,
          total_discount: 0,
          discounts: [],
          sku: null,
          grams: 0,
          vendor: "",
          taxable: false,
          product_has_only_default_variant: false,
          gift_card: false,
          final_price: 0,
          final_line_price: 0,
          url: "",
          requires_shipping: false,
          product_type: "",
          product_description: "",
          variant_options: [],
          options_with_values: [],
          line_level_discount_allocations: [],
          line_level_total_discount: 0,
          quantity_rule: { min: 1, max: null, increment: 1 },
          has_components: false,
          title: "",
        };

        newItems = [...state.items, newItem];
      }

      const { totalItems, subtotal } = calculateTotals(newItems);
      return {
        ...state,
        items: newItems,
        totalItems,
        subtotal,
      };
    }
    case "OPTIMISTIC_REMOVE": {
      const newItems = state.items.filter(
        (item) => item.variant_id !== action.payload.productId,
      );
      const { totalItems, subtotal } = calculateTotals(newItems);
      return {
        ...state,
        items: newItems,
        totalItems,
        subtotal,
      };
    }
    case "OPTIMISTIC_UPDATE": {
      const { productId, quantity } = action.payload;
      const newItems = state.items.map((item) =>
        item.variant_id === productId ? { ...item, quantity } : item,
      );
      const { totalItems, subtotal } = calculateTotals(newItems);
      return {
        ...state,
        items: newItems,
        totalItems,
        subtotal,
      };
    }
    case "OPTIMISTIC_CLEAR":
      return {
        ...state,
        items: [],
        totalItems: 0,
        subtotal: 0,
      };
    default:
      return state;
  }
};

// Create context with undefined default value
const CartContext = createContext<CartContextType | undefined>(undefined);

// Cart provider component
export const CartProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Use reducer for complex state management
  const [state, dispatch] = useReducer(cartReducer, {
    items: [],
    totalItems: 0,
    subtotal: 0,
    isOpen: false,
    isLoading: false,
    error: null,
    inventoryWarnings: {},
  });

  // API interaction functions
  const fetchCart = useCallback(async () => {
    dispatch({ type: "SET_LOADING", payload: true });
    try {
      // Use GraphQL query GetCart($cartId: ID!) instead of REST API
      const response = await getShopifyCart();

      if (response?.lines?.edges) {
        // Transform Shopify GraphQL response to match existing cart item structure
        const items: IApiCartItem[] = response.lines.edges.map((edge: any) => ({
          id: edge.node.id,
          variant_id: edge.node.merchandise.id,
          quantity: edge.node.quantity,
          price: Number.parseFloat(edge.node.merchandise.price.amount) * 100, // Convert to cents
          original_price:
            Number.parseFloat(edge.node.merchandise.price.amount) * 100,
          image: edge.node.merchandise.image?.url || "",
          featured_image: {
            url: edge.node.merchandise.image?.url || "",
          },
          handle: edge.node.merchandise.product.handle,
          variant_title: edge.node.merchandise.title,
          product_id: edge.node.merchandise.product.id,
          product_title: edge.node.merchandise.product.title,
          // Add other required fields with default values
          key: edge.node.id,
          properties: {},
          presentment_price:
            Number.parseFloat(edge.node.merchandise.price.amount) * 100,
          discounted_price:
            Number.parseFloat(edge.node.merchandise.price.amount) * 100,
          line_price:
            Number.parseFloat(edge.node.merchandise.price.amount) *
            edge.node.quantity *
            100,
          original_line_price:
            Number.parseFloat(edge.node.merchandise.price.amount) *
            edge.node.quantity *
            100,
          total_discount: 0,
          discounts: [],
          sku: null,
          grams: 0,
          vendor: "",
          taxable: false,
          product_has_only_default_variant: false,
          gift_card: false,
          final_price:
            Number.parseFloat(edge.node.merchandise.price.amount) * 100,
          final_line_price:
            Number.parseFloat(edge.node.merchandise.price.amount) *
            edge.node.quantity *
            100,
          url: "",
          requires_shipping: false,
          product_type: "",
          product_description: "",
          variant_options: [],
          options_with_values: [],
          line_level_discount_allocations: [],
          line_level_total_discount: 0,
          quantity_rule: { min: 1, max: null, increment: 1 },
          has_components: false,
          title: edge.node.merchandise.title,
        }));

        const { totalItems, subtotal } = calculateTotals(items);

        dispatch({
          type: "SET_CART_DATA",
          payload: { items, totalItems, subtotal },
        });
      } else {
        dispatch({
          type: "SET_CART_DATA",
          payload: { items: [], totalItems: 0, subtotal: 0 },
        });
      }
    } catch (error) {
      console.error("Error fetching cart:", error);
      dispatch({ type: "SET_ERROR", payload: "Failed to load cart data" });
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, []);

  // Load cart on initial render
  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  // Cart visibility functions
  const openCart = useCallback(() => {
    dispatch({ type: "TOGGLE_CART", payload: true });
  }, []);

  const closeCart = useCallback(() => {
    dispatch({ type: "TOGGLE_CART", payload: false });
    dispatch({ type: "SET_ERROR", payload: null }); // Clear error when closing cart
  }, []);

  const toggleCart = useCallback(() => {
    dispatch({ type: "TOGGLE_CART" });
  }, []);

  // Cart operation with error handling
  const performCartOperation = useCallback(
    async (
      operation: () => Promise<void>,
      optimisticAction: CartAction,
      errorMessage: string,
    ) => {
      // Apply optimistic update
      dispatch(optimisticAction);

      dispatch({ type: "SET_LOADING", payload: true });
      try {
        await operation();
        // Refresh cart to sync with server
        await fetchCart();
      } catch (error) {
        console.error(errorMessage, error);
        dispatch({ type: "SET_ERROR", payload: errorMessage });
        // Refresh cart to revert optimistic update
        await fetchCart();
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [fetchCart],
  );

  // Add item to cart
  const addToCart = useCallback(
    async (
      variantId: string,
      quantity: number,
      productInfo?: Partial<IProduct>,
    ) => {
      if (quantity <= 0 || !variantId) {
        dispatch({
          type: "SET_ERROR",
          payload: "Invalid variant ID or quantity",
        });
        return;
      }

      // Create a minimal product object for the optimistic update
      const product: Partial<IProduct> = {
        ...productInfo,
        variants: [
          {
            id: variantId,
            title: productInfo?.variants?.[0]?.title || "",
            price: productInfo?.variants?.[0]?.price || "0",
            compare_at_price:
              productInfo?.variants?.[0]?.compare_at_price || "0",
            sku: productInfo?.variants?.[0]?.sku || "",
            barcode: productInfo?.variants?.[0]?.barcode || "",
            inventory_quantity:
              productInfo?.variants?.[0]?.inventory_quantity || 0,
            inventory_policy:
              productInfo?.variants?.[0]?.inventory_policy || "",
            inventory_management:
              productInfo?.variants?.[0]?.inventory_management || "",
            imageUrl: productInfo?.variants?.[0]?.imageUrl || "",
            presentment_prices:
              productInfo?.variants?.[0]?.presentment_prices || [],
            option_values: productInfo?.variants?.[0]?.option_values || [],
          },
        ],
      };

      await performCartOperation(
        async () => {
          await addToShopifyCart(variantId, quantity);

          // Trigger MoEngage event for add to cart
          publishEvent({
            type: EventType.ADD_TO_CART,
            productId: product.id || "",
            productName: product.title || "",
            price:
              typeof product.variants?.[0]?.price === "string"
                ? Number.parseFloat(product.variants[0].price) * 100
                : 0,
            quantity,
            variant: product.variants?.[0]?.title,
          });

          // Also trigger Shopify-specific add to cart event
          publishEvent({
            type: EventType.SHOPIFY_ADD_TO_CART,
            productId: product.id || "",
            productName: product.title || "",
            price:
              typeof product.variants?.[0]?.price === "string"
                ? Number.parseFloat(product.variants[0].price) * 100
                : 0,
            quantity,
            variant: product.variants?.[0]?.title,
          });
        },
        { type: "OPTIMISTIC_ADD", payload: { product, quantity } },
        "Failed to add item to cart",
      );

      // Open cart drawer after adding item
      openCart();
    },
    [performCartOperation, openCart],
  );

  // Remove item from cart
  const removeFromCart = useCallback(
    async (variantId: string) => {
      if (!variantId) {
        dispatch({
          type: "SET_ERROR",
          payload: "Variant ID not provided",
        });
        return;
      }

      await performCartOperation(
        async () => {
          // Find the line item for this variant
          const item = state.items.find(
            (item) => item.variant_id === variantId,
          );
          if (item?.id) {
            await removeCartItem(item.id);

            // Trigger MoEngage event for remove from cart
            publishEvent({
              type: EventType.REMOVE_FROM_CART,
              productId: item.product_id,
              productName: item.product_title,
              price: item.price,
              quantity: item.quantity,
              variant: item.variant_title,
            });

            // Also trigger Shopify-specific remove from cart event
            publishEvent({
              type: EventType.SHOPIFY_REMOVED_FROM_CART,
              productId: item.product_id,
              productName: item.product_title,
              price: item.price,
              quantity: item.quantity,
              variant: item.variant_title,
            });
          }
        },
        {
          type: "OPTIMISTIC_REMOVE",
          payload: { productId: variantId },
        },
        "Failed to remove item from cart",
      );
    },
    [performCartOperation, state.items],
  );

  // Update item quantity
  const updateQuantity = useCallback(
    async (variantId: string, quantity: number) => {
      if (!variantId) {
        dispatch({
          type: "SET_ERROR",
          payload: "Variant ID not provided",
        });
        return;
      }

      if (quantity <= 0) {
        await removeFromCart(variantId);
        return;
      }

      try {
        dispatch({ type: "SET_LOADING", payload: true });

        // Apply optimistic update
        dispatch({
          type: "OPTIMISTIC_UPDATE",
          payload: { productId: variantId, quantity },
        });

        // Find the line item for this variant
        const item = state.items.find((item) => item.variant_id === variantId);
        if (item?.id) {
          await updateCartItem(item.id, quantity);

          // Trigger MoEngage event for update cart
          publishEvent({
            type: EventType.UPDATE_CART,
            productId: item.product_id,
            productName: item.product_title,
            price: item.price,
            quantity: quantity,
            variant: item.variant_title,
          });

          // Also trigger Shopify-specific update cart event
          publishEvent({
            type: EventType.SHOPIFY_UPDATE_CART,
            productId: item.product_id,
            productName: item.product_title,
            price: item.price,
            quantity: quantity,
            variant: item.variant_title,
          });
        }

        // Clear error since Shopify handles inventory validation
        dispatch({ type: "SET_ERROR", payload: null });

        // Refresh cart to sync with server
        await fetchCart();
      } catch (error) {
        console.error("Failed to update item quantity", error);
        dispatch({
          type: "SET_ERROR",
          payload: "Failed to update item quantity",
        });
        // Refresh cart to revert optimistic update
        await fetchCart();
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [fetchCart, removeFromCart, state.items],
  );

  // Clear cart
  const clearCart = useCallback(async () => {
    await performCartOperation(
      async () => {
        // await clearCartApi();
      },
      { type: "OPTIMISTIC_CLEAR" },
      "Failed to clear cart",
    );
  }, [performCartOperation]);

  // Context value
  const value = {
    items: state.items,
    isOpen: state.isOpen,
    openCart,
    closeCart,
    toggleCart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    refreshCart: fetchCart,
    totalItems: state.totalItems,
    subtotal: state.subtotal,
    isLoading: state.isLoading,
    error: state.error,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

// Custom hook to use cart context
export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    console.error("useCart must be used within a CartProvider");
    // Return a default context instead of throwing
    return {
      items: [],
      isOpen: false,
      openCart: () => {},
      closeCart: () => {},
      toggleCart: () => {},
      addToCart: async () => {},
      removeFromCart: async () => {},
      updateQuantity: async () => {},
      clearCart: async () => {},
      refreshCart: async () => {},
      totalItems: 0,
      subtotal: 0,
      isLoading: false,
      error: null,
    };
  }
  return context;
};
